import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BillService } from '../../services/bill.service';
import { SortableHeaderComponent } from '../shared/sortable-header.component';
import { BillTypeBadgeComponent } from '../shared/bill-type-badge.component';
import { COLUMN_GROUPS, DEFAULT_VISIBLE_COLUMNS } from '../../constants';
import { BillFile, BillGroup, BillStatus, ColumnKey, ParsedBillData, BillType } from '../../models';
import { determineBillType, formatColumnLabel, formatColumnTooltip } from '../../utils/bill-utils';
import { formatBillDate } from '../../utils/date-format.util';

/** One display row in the bill table. */
interface BillRowEntry {
    key: string;
    bill: BillFile;
    pairPos?: 'first' | 'last';
    group?: BillGroup;
    pairTooltip?: string;
    rejected?: boolean;
}

@Component({
  selector: 'app-bill-list',
  standalone: true,
  imports: [
      CommonModule,
      MatCheckboxModule,
      MatTooltipModule,
      SortableHeaderComponent,
      BillTypeBadgeComponent
  ],
  templateUrl: './bill-list.component.html',
  styleUrl: './bill-list.component.scss'
})
export class BillListComponent {
    visibleColumns = input<Record<ColumnKey, boolean>>(DEFAULT_VISIBLE_COLUMNS);
    openColumnSelector = output<HTMLElement>();
    COLUMN_GROUPS = COLUMN_GROUPS;
    BillStatus = BillStatus;

    /**
     * Column header labels, formatted once at construction. Column keys come
     * from a module-level constant, so labels never change at runtime — no
     * reason to re-run formatColumnLabel on every CD cycle.
     */
    readonly columnLabels: Partial<Record<ColumnKey, string>> = COLUMN_GROUPS
        .flatMap(g => g.columns)
        .reduce((acc, key) => ({ ...acc, [key]: formatColumnLabel(key) }), {});

    /**
     * Optional long-form hover tooltip per column (only populated for columns
     * whose abbreviated header benefits from expansion — currently just the
     * three UAN columns). Everything else falls back to the visible label.
     */
    readonly columnTooltips: Partial<Record<ColumnKey, string>> = COLUMN_GROUPS
        .flatMap(g => g.columns)
        .reduce((acc, key) => {
            const tooltip = formatColumnTooltip(key);
            return tooltip ? { ...acc, [key]: tooltip } : acc;
        }, {});

    // Pre-computed warning tooltip text per bill (avoids method calls in template)
    billWarningText = computed(() => {
        const text: Record<string, string> = {};
        for (const bill of this.billService.processedBills()) {
            if (bill.warnings?.length) {
                text[bill.id] = bill.warnings.join('\n');
            }
        }
        return text;
    });

    billRetriable = this.billService.billRetriable;

    /**
     * Colspan for error rows: warning cell + all currently-visible data columns
     * (everything except the sticky "actions" column, which gets its own `<td>`).
     * Tracks the `visibleColumns` input so toggling column visibility in the
     * dashboard doesn't leave error rows under- or over-spanning.
     */
    errorColspan = computed(() => {
        const visible = this.visibleColumns();
        let count = 1; // warning-icon cell
        for (const group of COLUMN_GROUPS) {
            for (const key of group.columns) {
                if (visible[key] && key !== 'actions') count++;
            }
        }
        return count;
    });

    /**
     * Bills ordered for display: pair members adjacent, with the pair group
     * and a preformatted tooltip attached to the first member. The first
     * member renders a rowspan-2 "paired" spine cell spanning both rows.
     */
    readonly rowEntries = computed<BillRowEntry[]>(() => {
        const bills = this.billService.processedBills();
        const pairs = this.billService.pairedGroups();
        const rejected = this.billService.rejectedGroups();

        const pairByBillId = new Map<string, BillGroup>();
        for (const g of [...pairs, ...rejected]) {
            if (!g.utilityBill || !g.supplierBill) continue;
            pairByBillId.set(g.utilityBill.id, g);
            pairByBillId.set(g.supplierBill.id, g);
        }

        const entries: BillRowEntry[] = [];
        const placed = new Set<string>();
        for (const bill of bills) {
            if (placed.has(bill.id)) continue;
            const group = pairByBillId.get(bill.id);
            const partnerId = group ? (group.utilityBill!.id === bill.id ? group.supplierBill!.id : group.utilityBill!.id) : null;
            const partner = partnerId ? bills.find(b => b.id === partnerId) : null;
            if (group && partner) {
                const currency = group.utilityBill!.data?.currency || 'USD';
                const combined = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(group.combinedAmount);
                const first = group.utilityBill!.id === bill.id ? bill : partner;
                const second = first === bill ? partner : bill;
                const period = `${formatBillDate(group.billingPeriod.start)} - ${formatBillDate(group.billingPeriod.end)}`;
                entries.push({
                    key: 'b:' + first.id, bill: first, pairPos: 'first', group,
                    rejected: !!group.rejected,
                    pairTooltip: group.rejected
                        ? `Unpaired bills\n${period}\nClick to re-pair`
                        : `Paired bills\n${period}\n${combined} combined`
                });
                entries.push({ key: 'b:' + second.id, bill: second, pairPos: 'last', rejected: !!group.rejected });
                placed.add(bill.id);
                placed.add(partner.id);
            } else {
                entries.push({ key: 'b:' + bill.id, bill });
                placed.add(bill.id);
            }
        }
        return entries;
    });

    constructor(public billService: BillService) {}

    // Helper to check if a column is the first visible column in its group
    isFirstVisibleInGroup(groupIndex: number, colIndex: number, columns: ColumnKey[]): boolean {
        if (groupIndex === 0) return false; // First group never gets a section-start border
        const visible = this.visibleColumns();
        for (let i = 0; i < columns.length; i++) {
            if (visible[columns[i]]) {
                return i === colIndex;
            }
        }
        return false;
    }

    // Helper to count visible columns in a group (for colspan)
    getVisibleColumnCount(columns: ColumnKey[]): number {
        const visible = this.visibleColumns();
        return columns.filter(col => visible[col]).length;
    }

    getBillType(data: ParsedBillData | null): BillType {
        return determineBillType(data);
    }

    getBillDataValue(bill: BillFile, key: string): any {
        if (!bill.data) return '-';

        switch (key) {
            case 'startDate': return bill.data.billingPeriodStart ? formatBillDate(bill.data.billingPeriodStart) : '-';
            case 'endDate': return bill.data.billingPeriodEnd ? formatBillDate(bill.data.billingPeriodEnd) : '-';
            case 'usage': return bill.data.usageAmount ? `${bill.data.usageAmount.toLocaleString()} ${bill.data.usageUnit || ''}` : '-';
            case 'amount': return bill.data.totalAmount ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.totalAmount)) : '-';
            case 'utilityAccountNumber': return bill.data.utilityAccountNumber || '-';
            case 'utilityAccountNumber2': return bill.data.utilityAccountNumber2 || '-';
            case 'confidence': return bill.data.confidence ? `${Math.round(bill.data.confidence * 100)}%` : '-';
            case 'supplyCharges': return bill.data.supplyCharges ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.supplyCharges)) : '-';
            case 'supplyTaxes': return bill.data.supplyTaxes ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.supplyTaxes)) : '-';
            case 'distributionCharges': return bill.data.distributionCharges ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(bill.data.distributionCharges)) : '-';
            case 'distributionTaxes': {
                const taxes = bill.data.distributionTaxes;
                if (typeof taxes === 'number') {
                    return taxes ? (new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(taxes)) : '-';
                }
                return '-';
            }
            // Pro mode charge groups
            case 'supplyContractCharges':
            case 'supplyEnergyCharges':
            case 'supplyCapacityCharges':
            case 'supplyTransmissionCharges':
            case 'supplyMarketCharges':
            case 'supplyLineLossCharges':
            case 'supplyGrtSut':
            case 'supplyMiscCharges':
            case 'supplySalesTaxes':
            case 'distributionDemandCharges':
            case 'distributionEnergyCharges':
            case 'distributionCustomerCharges':
            case 'distributionMiscCharges':
            case 'distributionTaxCharges': {
                const chargeGroup = (bill.data as any)[key];
                if (chargeGroup && typeof chargeGroup === 'object' && 'total' in chargeGroup) {
                    return new Intl.NumberFormat('en-US', { style: 'currency', currency: bill.data.currency || 'USD' }).format(chargeGroup.total);
                }
                return '-';
            }
            default: return (bill.data as any)[key] || '-';
        }
    }

    getUtilityDisplay(data: ParsedBillData | null): string {
        if (!data) return '-';
        if (data.utilityName) return data.utilityName;
        if (data.billType === 'supplier') return 'Supplier Bill Only';
        return '-';
    }

    toggleSelectAll() {
        // Logic to toggle all bills selection
        const allIds = this.billService.processedBills().map(b => b.id);
        const currentSelection = this.billService.selectedBillIds();

        if (currentSelection.length === allIds.length && allIds.length > 0) {
            this.billService.clearSelection();
        } else {
            allIds.forEach(id => {
                if (!currentSelection.includes(id)) this.billService.toggleSelection(id, 'bill');
            });
        }
    }

    onRetry(bill: BillFile) {
        // Retry processing the failed bill
        this.billService.processBill(bill.id, bill.file);
    }
}
